import { Box, Button, Stack, styled, Typography } from "@mui/material";
import {
  ASTableV2,
  ASTableV2Body,
  ASTableV2Cell,
  ASTableV2Header,
  ASTableV2Row,
  ASTableV2RowEmpty,
  ErrorPanel,
  formatUtcDate
} from "@qubit/autoparts";
import { useMemo, useState } from "react";
import { useTranslation } from "react-i18next";

import { useAppSelector } from "~/app/store";
import { useDisclosure } from "~/hooks/useDisclosure";
import { extractUserNameFromAuth0Id } from "~/lib/helpers";

import { getMessageFromRtkError } from "~/lib/rtkErrorToMessage";

import { selectUserHasApiTokenAccessRole } from "~/redux/selectors/authSelectors";
import { useGetApiTokensQuery } from "~/redux/warehouse/apiTokens.hooks";

import CreateApiTokenModal from "./CreateApiTokenModal";

const ApiTokensTableContainer = styled(Box)(({ theme }) => ({
  backgroundColor: theme.palette.background.default,
  border: `1px solid ${theme.palette.gray.dark}`,
  borderRadius: "10px",
  m: 2,
  overflowX: "scroll",
  "&::-webkit-scrollbar": {
    display: "none"
  },
  scrollbarWidth: "none",
  msOverflowStyle: "none"
}));

const ApiTokensView = () => {
  const { t } = useTranslation();
  const [currentPage, setPage] = useState<number>(1);
  const limit = 10;
  const userHasApiTokenAccessRole = useAppSelector(
    selectUserHasApiTokenAccessRole
  );
  const {
    open: isApiTokenCreationModalOpen,
    onOpen: onApiTokenCreationModalOpen,
    onClose: onApiTokenCreationModalClose
  } = useDisclosure();

  const {
    apiTokensData = [],
    error,
    areApiTokensFetching,
    refetch: refetchApiTokensList
  } = useGetApiTokensQuery(undefined, {
    refetchOnMountOrArgChange: true,
    selectFromResult: ({ data, error, isFetching }) => ({
      apiTokensData: data?.toSorted((a, b) => {
        const dateA = a.createdOn
          ? typeof a.createdOn === "string"
            ? new Date(a.createdOn).getTime()
            : a.createdOn.getTime()
          : null;
        const dateB = a.createdOn
          ? typeof b.createdOn === "string"
            ? new Date(b.createdOn).getTime()
            : a.createdOn.getTime()
          : null;
        return (
          (dateA && dateB && dateB - dateA) || a.name.localeCompare(b.name)
        );
      }),
      error,
      areApiTokensFetching: isFetching
    })
  });

  const totalPageCount = apiTokensData.length
    ? Math.ceil(apiTokensData.length / limit)
    : 0;
  const apiTokensToDisplay = useMemo(() => {
    return apiTokensData.slice((currentPage - 1) * limit, limit * currentPage);
  }, [apiTokensData, currentPage]);

  return (
    <Stack spacing={4} p={2}>
      <Box>
        <Typography variant="h6">{t("organization api tokens")}</Typography>
        <Typography variant="subtitle1">
          {t("organization api tokens description")}
        </Typography>
      </Box>
      <Button
        color="primary"
        sx={{
          minWidth: 200,
          alignSelf: "flex-end"
        }}
        onClick={onApiTokenCreationModalOpen}
        disabled={!userHasApiTokenAccessRole}
      >
        {t("create api token")}
      </Button>
      {error && (
        <ErrorPanel
          variant="outline"
          message={getMessageFromRtkError(error)}
          sx={{
            backgroundColor: "error.light"
          }}
          tryAgain={{
            onTryAgain: async () => {
              await refetchApiTokensList();
            }
          }}
        />
      )}
      {!error && (
        <ApiTokensTableContainer>
          <ASTableV2
            gridTemplateColumns={`repeat(5, auto)`}
            isLoading={areApiTokensFetching}
            pagination={{
              pageCount: totalPageCount,
              page: currentPage,
              setPage: (pageNumber) => {
                setPage(pageNumber);
              }
            }}
          >
            <ASTableV2Header>
              <ASTableV2Row>
                <ASTableV2Cell>
                  <Typography variant="tableBody">{t("name")}</Typography>
                </ASTableV2Cell>
                <ASTableV2Cell>
                  <Typography variant="tableBody">{t("created by")}</Typography>
                </ASTableV2Cell>
                <ASTableV2Cell>
                  <Typography variant="tableBody">{t("roles")}</Typography>
                </ASTableV2Cell>
                <ASTableV2Cell>
                  <Typography variant="tableBody">{t("created on")}</Typography>
                </ASTableV2Cell>
                <ASTableV2Cell>
                  <Typography variant="tableBody">{t("expires on")}</Typography>
                </ASTableV2Cell>
              </ASTableV2Row>
            </ASTableV2Header>
            <ASTableV2Body>
              {!areApiTokensFetching && !apiTokensData.length && (
                <ASTableV2RowEmpty>
                  {t("no api tokens found")}
                </ASTableV2RowEmpty>
              )}
              {apiTokensToDisplay.map((apiToken, index) => {
                const { name, userId, expiresOn, createdOn, role } = apiToken;
                const createdOnDate = formatUtcDate(createdOn) || "-";
                const expiresOnDate = formatUtcDate(expiresOn) || "-";
                const roles = role.join(", ");
                return (
                  <ASTableV2Row key={`${name}${index}-token`}>
                    <ASTableV2Cell>
                      <Typography variant="tableBody">{name}</Typography>
                    </ASTableV2Cell>
                    <ASTableV2Cell>
                      <Typography variant="tableBody">
                        {extractUserNameFromAuth0Id(userId)}
                      </Typography>
                    </ASTableV2Cell>
                    <ASTableV2Cell>
                      <Typography variant="tableBody">{roles}</Typography>
                    </ASTableV2Cell>
                    <ASTableV2Cell>
                      <Typography variant="tableBody">
                        {createdOnDate}
                      </Typography>
                    </ASTableV2Cell>
                    <ASTableV2Cell>
                      <Typography variant="tableBody">
                        {expiresOnDate}
                      </Typography>
                    </ASTableV2Cell>
                  </ASTableV2Row>
                );
              })}
            </ASTableV2Body>
          </ASTableV2>
        </ApiTokensTableContainer>
      )}
      {isApiTokenCreationModalOpen && (
        <CreateApiTokenModal
          open={isApiTokenCreationModalOpen}
          onClose={onApiTokenCreationModalClose}
        />
      )}
    </Stack>
  );
};

export default ApiTokensView;
